'use client'

import React, { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { EASE_OUT } from './motion'
import LcIcon from './icons'
import { useAnimatedClip } from './useAnimatedClip'

type Props = {
  open: boolean
  children: React.ReactNode
  className?: string
  /** Fades the bottom edge while collapsed — for clamped text rather than whole sections. */
  fadeMask?: boolean
}

/** Height + opacity disclosure: 220ms strong ease-out in, 170ms out; opacity only under reduced motion. */
export const Collapsible = ({ open, children, className, fadeMask }: Props) => {
  const reduced = useReducedMotion()
  const clip = useAnimatedClip()
  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          key="content"
          initial={reduced ? { opacity: 0 } : { height: 0, opacity: 0 }}
          animate={reduced ? { opacity: 1 } : { height: 'auto', opacity: 1 }}
          exit={reduced ? { opacity: 0 } : { height: 0, opacity: 0 }}
          transition={{ duration: open ? 0.22 : 0.17, ease: EASE_OUT }}
          {...clip}
          className={cn(
            fadeMask && '[mask-image:linear-gradient(to_bottom,#000_85%,transparent)]',
            className
          )}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

type ClampProps = {
  children: React.ReactNode
  /** Lines shown while collapsed. */
  lines?: number
  moreLabel?: string
  lessLabel?: string
  className?: string
}

/**
 * Text that folds to `lines` and expands with a height animation. The toggle only appears
 * when the content actually overflows, so short text never grows a dead control.
 */
export const ClampText = ({
  children,
  lines = 4,
  moreLabel = 'Read more',
  lessLabel = 'Show less',
  className,
}: ClampProps) => {
  const [expanded, setExpanded] = useState(false)
  const [overflows, setOverflows] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const check = () => setOverflows(el.scrollHeight - el.clientHeight > 2)
    check()
    const observer = new ResizeObserver(check)
    observer.observe(el)
    return () => observer.disconnect()
  }, [children])

  return (
    <div className={className}>
      <div
        ref={ref}
        className={cn(
          'text-[14px] leading-[1.5] tracking-[-0.2px] text-ink-3 transition-[max-height] duration-[220ms] ease-out-strong',
          !expanded && 'overflow-hidden'
        )}
        style={
          !expanded
            ? { display: '-webkit-box', WebkitLineClamp: lines, WebkitBoxOrient: 'vertical' }
            : undefined
        }
      >
        {children}
      </div>
      {(overflows || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1.5 inline-flex items-center gap-1 rounded-[6px] text-[12px] font-medium leading-none tracking-[-0.2px] text-brand outline-none transition-colors duration-[120ms] hover:text-brand-2 focus-visible:ring-2 focus-visible:ring-brand/40"
        >
          {expanded ? lessLabel : moreLabel}
          <LcIcon
            name="chevron-down"
            size={12}
            className={cn(
              'transition-transform duration-[200ms] ease-out-strong',
              expanded && 'rotate-180'
            )}
          />
        </button>
      )}
    </div>
  )
}

export default Collapsible
